(() => {
  const config = document.querySelector('[data-fbt-size-sync]');
  if (!config) return;
  const { sectionId, sizeIndex, initialSize } = JSON.parse(config.textContent);
  const main = document.getElementById(`shopify-section-${sectionId}`);
  if (!main) return;
  const selector = '.Avada-OfferItem.-fbt .Avada-VariantSelection__Trigger[aria-label^="Size:"]';
  const normalize = value => {
    const text = String(value || '').trim().toUpperCase();
    return text.match(/^(XXS|XS|S|M|L|XL|XXL|XXXL|[2-6]XL)(?=$|\s|\()/)?.[1] || text;
  };
  const cbbSelector = 'select.cbb-recommendations-variant-select';
  const productCache = new Map();
  const controls = () => [...main.querySelectorAll(selector), ...document.querySelectorAll(cbbSelector)];
  const attempted = new WeakMap();
  let size = normalize(initialSize);
  let revision = 0;
  let running = false;
  let scheduled = false;
  const pause = () => new Promise(resolve => setTimeout(resolve, 50));

  function cbbRow(select) {
    for (let row = select.parentElement; row && row !== document.body; row = row.parentElement) {
      if (row.querySelectorAll('.cbb-frequently-bought-selector-input').length === 1 &&
          (select.matches('.cbb-frequently-bought-this-item-variant-select') ||
            row.querySelector('a.cbb-frequently-bought-selector-link'))) return row;
    }
    return null;
  }

  function notice(trigger, message) {
    const item = trigger.matches(cbbSelector) ? cbbRow(trigger) : trigger.closest('.Avada-OfferItem');
    if (!item) return;
    let warning = item.querySelector('[data-combo-size-warning]');
    if (!message) {
      warning?.remove();
      return;
    }
    if (!warning) {
      warning = document.createElement('p');
      warning.dataset.comboSizeWarning = '';
      warning.setAttribute('role', 'status');
      item.append(warning);
    }
    warning.textContent = message;
  }

  async function selectSize(trigger, expected, version) {
    if (normalize(trigger.getAttribute('aria-label').split(':').slice(1).join(':')) === expected) {
      notice(trigger, '');
      return;
    }
    if (trigger.getAttribute('aria-expanded') === 'true') return;

    // Use the app's keyboard and option handlers so its variant state is updated.
    trigger.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
    let list;
    for (let count = 0; count < 20; count++) {
      await pause();
      if (!trigger.isConnected || version !== revision) break;
      list = document.getElementById(trigger.getAttribute('aria-controls'));
      if (list) break;
    }
    try {
      if (!trigger.isConnected || version !== revision) return;
      const option = Array.from(list?.querySelectorAll('[role="option"]') || []).find(
        node => normalize(node.textContent) === expected &&
          node.getAttribute('aria-disabled') !== 'true' && !node.hasAttribute('disabled')
      );
      if (option) {
        option.click();
        await pause();
      }
      const matched = normalize(trigger.getAttribute('aria-label').split(':').slice(1).join(':')) === expected;
      notice(trigger, matched ? '' : `Size ${expected} could not be selected. Please choose an available size for this combo item.`);
    } finally {
      if (trigger.isConnected && trigger.getAttribute('aria-expanded') === 'true') {
        trigger.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      }
    }
  }

  async function selectCbbSize(select, expected, version) {
    const row = cbbRow(select);
    if (!row) return;
    try {
      // CBB renders the main product title without a link.
      const productUrl = select.matches('.cbb-frequently-bought-this-item-variant-select')
        ? location.href
        : row.querySelector('a.cbb-frequently-bought-selector-link').href;
      const url = new URL(productUrl, location.href);
      const match = url.pathname.match(/(?:^|\/)products\/([^/]+)\/?$/);
      if (!match) throw new Error('Missing product handle');
      // Use the current storefront origin, including Shopify CLI's local proxy.
      const path = `${window.Shopify?.routes?.root || '/'}products/${match[1]}.js`;
      if (!productCache.has(path)) {
        productCache.set(path, fetch(path, { signal: AbortSignal.timeout(10000) }).then(response => {
          if (!response.ok) throw new Error('Unable to load product variants');
          return response.json();
        }).catch(error => { productCache.delete(path); throw error; }));
      }
      const product = await productCache.get(path);
      if (!select.isConnected || version !== revision) return;
      const index = product.options.findIndex(option => String(option.name || option).trim().toLowerCase() === 'size');
      if (index < 0) return;
      const currentId = select.selectedOptions[0]?.dataset.variantId;
      const current = product.variants.find(variant => String(variant.id) === currentId);
      if (!current) throw new Error('Unknown selected variant');
      const target = product.variants.find(variant => variant.available &&
        normalize(variant.options[index]) === expected &&
        variant.options.every((value, position) => position === index || value === current.options[position]) &&
        Array.from(select.options).some(option => option.dataset.variantId === String(variant.id) && !option.disabled)
      );
      if (!target) {
        notice(select, `Size ${expected} is unavailable. Please choose an available size or uncheck this item.`);
        return;
      }
      notice(select, '');
      if (String(target.id) !== currentId) {
        // CBB uses array indexes as option values; the variant ID is a data attribute.
        select.value = Array.from(select.options).find(option => option.dataset.variantId === String(target.id)).value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (error) {
      if (select.isConnected && version === revision) {
        notice(select, 'Unable to sync size. Please choose an available size or uncheck this item.');
      }
    }
  }

  async function synchronize() {
    scheduled = false;
    if (running || !size) return;
    running = true;
    try {
      const version = revision;
      for (const trigger of controls()) {
        if (version !== revision) break;
        if (!trigger.isConnected || attempted.get(trigger) === version) continue;
        attempted.set(trigger, version);
        try {
          if (trigger.matches(cbbSelector)) {
            await selectCbbSize(trigger, size, version);
          } else {
            await selectSize(trigger, size, version);
          }
        } catch (error) {
          // One app control failing must not prevent the remaining items from syncing.
          if (trigger.isConnected && version === revision) {
            notice(trigger, 'Unable to sync size. Please choose an available size for this combo item.');
          }
        }
      }
    } finally {
      running = false;
      if (controls().some(el => attempted.get(el) !== revision)) schedule();
    }
  }

  function schedule() {
    if (scheduled || running) return;
    scheduled = true;
    requestAnimationFrame(synchronize);
  }

  document.addEventListener(`eurus:product-card-variant-select:updated:${sectionId}`, event => {
    const nextSize = normalize(event.detail.currentVariant?.options?.[sizeIndex]);
    if (!nextSize || nextSize === size) return;
    size = nextSize;
    revision++;
    schedule();
  });

  // Observe delayed app mounts without repeatedly overriding manual selections.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('click', event => {
    const button = event.target.closest('.Avada-Offer__ButtonAddToCart, .cbb-frequently-bought-add-button');
    if (!button) return;
    const warning = button.matches('.cbb-frequently-bought-add-button')
      ? Array.from(document.querySelectorAll('[data-combo-size-warning]')).find(node =>
          node.parentElement.querySelector('.cbb-frequently-bought-selector-input')?.checked)
      : button.closest('.Avada-OfferLayout')?.querySelector('.Avada-OfferItem.isSelected [data-combo-size-warning]');
    if (warning || running) {
      event.preventDefault();
      event.stopImmediatePropagation();
      warning?.scrollIntoView({ block: 'nearest' });
    }
  }, true);
  document.addEventListener('click', event => {
    if (!event.isTrusted) return;
    const option = event.target.closest('.Avada-VariantSelection__Option');
    const list = option?.closest('[role="listbox"]');
    if (!list) return;
    for (const trigger of main.querySelectorAll(selector)) {
      if (trigger.getAttribute('aria-controls') === list.id && option.getAttribute('aria-disabled') !== 'true') {
        notice(trigger, '');
      }
    }
  }, true);
  document.addEventListener('change', event => {
    if (event.isTrusted && event.target.matches(cbbSelector)) notice(event.target, '');
  });
  schedule();
})();
